import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Capacitor } from "@capacitor/core";

// Extracted pages
import { 
  TeamPage, HomePage, LastTrainingPage, RateSessionPage, SessionsPage, StatisticsPage, 
  CoachesPage, MyGoalPage, ProfilePage, TeamGoalsPage, TeamSessionsPage,
  AgendaPage, AccountSelectionPage, LoginPage, ResetPasswordPage, ClubDashboardPage,
  AdminLoginPage, AdminDashboardPage, CoCoachPage, AIAdvicePage
} from './pages';
import PrivacyPage from "./pages/PrivacyPage";
import ClubExercisesPage from "./pages/ClubExercisesPage";
import SessionPlannerPage from "./pages/SessionPlannerPage";
import PlannerSessionPage from "./pages/PlannerSessionPage";
import PlannerExercisePage from "./pages/PlannerExercisePage";
import SessionsDesignerGate from './components/planner/SessionsDesignerGate';
import ChatPage from "./pages/ChatPage";
import ActivityReminderManager from './components/system/ActivityReminderManager';
import ReminderNavigation from './components/system/ReminderNavigation';

// Shared context
import { UserProvider, useUser } from './context/UserContext';

// Hide admin portal in native (iOS/Android) builds — admin remains browser-only at mygoalapp.com/admin
const IS_NATIVE = Capacitor.isNativePlatform();

function App() {
  return (
    <div className="App">
      <UserProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </UserProvider>
      <Toaster position="top-center" richColors />
    </div>
  );
}

// Separate component to use hooks inside UserProvider
const AppRoutes = () => {
  const { isLoggedIn, loading, isClub, sessionError, retrySession } = useUser();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="text-white/50">Loading...</div>
      </div>
    );
  }

  if (sessionError && !isLoggedIn) {
    return <main className="flex min-h-screen items-center justify-center bg-[#0a0a0f] p-6 text-white">
      <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-[#1a1a2e] p-6 text-center" role="alert">
        <h1 className="text-xl font-bold">Let’s reconnect</h1>
        <p className="mt-3 text-sm leading-6 text-white/60">{sessionError}</p>
        <button onClick={retrySession} className="mt-6 w-full rounded-xl bg-gradient-to-r from-[#493986] to-[#92a7e8] py-3 font-semibold">Try again</button>
      </div>
    </main>;
  }

  return (
    <>
      <ActivityReminderManager />
      <ReminderNavigation />
      <div className="pt-safe">
        <Routes>
          {/* Public routes */}
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/welcome" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={isLoggedIn ? <Navigate to="/" replace /> : <LoginPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/register" element={<AccountSelectionPage />} />
          
          {/* Hidden admin routes — web only, NOT available in native (iOS/Android) builds */}
          {!IS_NATIVE && (
            <>
              <Route path="/admin" element={<AdminLoginPage />} />
              <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
            </>
          )}
          {IS_NATIVE && (
            <>
              <Route path="/admin" element={<Navigate to="/welcome" replace />} />
              <Route path="/admin/dashboard" element={<Navigate to="/welcome" replace />} />
            </>
          )}
          
          {/* Protected routes - redirect directly to login if not logged in */}
          <Route path="/" element={isLoggedIn ? (isClub ? <ClubDashboardPage /> : <HomePage />) : <Navigate to="/login" replace />} />
          <Route path="/last-training" element={isLoggedIn ? <LastTrainingPage /> : <Navigate to="/login" replace />} />
          <Route path="/rate-session/:sessionId" element={isLoggedIn ? <RateSessionPage /> : <Navigate to="/login" replace />} />
          <Route path="/sessions" element={isLoggedIn ? <SessionsPage /> : <Navigate to="/login" replace />} />
          <Route path="/mygoal" element={isLoggedIn ? <MyGoalPage /> : <Navigate to="/login" replace />} />
          <Route path="/club-exercises" element={isLoggedIn ? <ClubExercisesPage /> : <Navigate to="/" replace />} />
          <Route path="/session-planner" element={<SessionsDesignerGate><SessionPlannerPage /></SessionsDesignerGate>} />
          <Route path="/session-planner/sessions/:sessionId" element={<SessionsDesignerGate><PlannerSessionPage /></SessionsDesignerGate>} />
          <Route path="/session-planner/exercises/:exerciseId" element={<SessionsDesignerGate><PlannerExercisePage /></SessionsDesignerGate>} />
          <Route path="/chat" element={isLoggedIn ? <ChatPage /> : <Navigate to="/login" replace />} />
          <Route path="/coaches" element={isLoggedIn ? <CoachesPage /> : <Navigate to="/login" replace />} />
          <Route path="/team" element={isLoggedIn ? <TeamPage /> : <Navigate to="/login" replace />} />
          <Route path="/team/sessions" element={isLoggedIn ? <TeamSessionsPage /> : <Navigate to="/login" replace />} />
          <Route path="/team/goals" element={isLoggedIn ? <TeamGoalsPage /> : <Navigate to="/login" replace />} />
          <Route path="/agenda" element={isLoggedIn ? <AgendaPage /> : <Navigate to="/login" replace />} />
          <Route path="/ai-advice" element={isLoggedIn ? <AIAdvicePage /> : <Navigate to="/login" replace />} />
          <Route path="/statistics" element={isLoggedIn ? <StatisticsPage /> : <Navigate to="/login" replace />} />
          <Route path="/profile" element={isLoggedIn ? <ProfilePage /> : <Navigate to="/login" replace />} />
          <Route path="/club" element={isLoggedIn ? <ClubDashboardPage /> : <Navigate to="/login" replace />} />
          <Route path="/co-coach" element={isLoggedIn ? <CoCoachPage /> : <Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </>
  );
};

export default App;
